/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2018 ICEMEDX, Inc.
***********************************************/
import { ViewChild, ElementRef, Renderer2, RendererFactory2, Component, Input, Output, Inject, OnInit, EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { IcepanelService } from './icepanel.service';
import { IcepanelContainerComponent } from './icepanelcontainer.component';
import { IcecontrolSettings, IcepanelSettings, IcepanelsectionSettings, IcecontrolPermission } from '../models/icesettings';
import { userTypes } from '../../login/login.config';

@Component({
    selector: 'icepanel',
    templateUrl: './icepanel.component.html',
    styleUrls: ['./icepanelcontainer.component.scss']
})
export class IcepanelComponent implements OnInit {
  @Input() panelTag: string = "";
  @Input() panelKey: string = "";
  @Input() title: string = "";
  @Input() controlId: string = "";
  @Input() sections: any = [];
  @Input() headerControls: any = [];
  @Input() panelSettings: IcepanelSettings = null;

  @Output() headerClick: EventEmitter<any> = new EventEmitter<any>();

  // Mat Grid outputs
  @Output() matgridRowClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() matgridRowActionClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() matgridColClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() matgridAddRowButtonClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() tableLazyLoad: EventEmitter<any> = new EventEmitter<any>();

  // Control outputs
  @Output() buttonClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() controlChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() controlClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() fileUploadSuccessClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() fileRemoveClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() keyUp: EventEmitter<any> = new EventEmitter<any>();
  @Output() keydownEnter: EventEmitter<any> = new EventEmitter<any>();
  @Output() keydownTab: EventEmitter<any> = new EventEmitter<any>();

  private panelElem: HTMLDivElement = null;
	id: string = "";
	headerId: string = "";
  bodyId: string = "";
  disableHeaderControls: boolean = false;
  disableSubmit: boolean = false;
  permission: IcecontrolPermission = null;

  constructor(private targetRef: ElementRef,
    @Inject(IcepanelContainerComponent) private parent: IcepanelContainerComponent,
    private panelSvc: IcepanelService,
    private rendererFactory: RendererFactory2) {
  }

  ngOnInit() {
    this.permission = this.panelSettings.permissions ? this.panelSettings.permissions : new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaNone);
    this.setPermission();

    if (this.permission.default_access === userTypes.USER_FEATURE_ACCESS.ufaReadOnly) {
      this.disableHeaderControls = true;
    }

    let panelDiv = this.panelSvc.getPanel(this.targetRef.nativeElement);

    if (panelDiv) {
      this.id = this.title + "_" + this.panelTag + "_Panel_" + this.panelKey;
      let renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);
      renderer.setAttribute(panelDiv, "paneltag", this.panelTag);
      this.headerId = this.id + "_Header";
      this.bodyId = this.id + "_Body";
    }
  }

  getPanel(): IcepanelSettings {
    return this.panelSettings;
  }

  getSections(): IcepanelsectionSettings {
    return this.sections;
  }

  getContainer(): IcepanelContainerComponent {
    return this.parent;
  }

  isContainerInUse(): boolean {
    return this.parent != null && this.parent.settings != null && this.parent.settings.rows != null && this.parent.settings.rows.length > 0;
  }

  hasAccess(): boolean {
    return (this.permission.default_access !== userTypes.USER_FEATURE_ACCESS.ufaNone || this.permission.feature === userTypes.USER_FEATURES.ufNone);
  }

  onHeaderActionClick(event, ctrl: IcecontrolSettings) {
    event.headerCtrl = ctrl == null ? this.panelSettings : ctrl;
    if (this.isContainerInUse() && this.parent.headerClick)
      this.parent.headerClick.emit(event);
    else
      this.headerClick.emit(event);
  }

  onFormSubmit(event) {
    if (this.disableSubmit) return;

    this.disableSubmit = true;
    try {
      setTimeout(() => {
        event.headerCtrl = this.panelSettings;
        if (this.isContainerInUse() && this.parent.headerClick)
          this.parent.headerClick.emit(event);
        else
          this.headerClick.emit(event);

      });
    }
    catch (e) {
      console.log(e);
    }
    this.disableSubmit = false;
  }

  onMatGridRowClick(event) {
    if (this.isContainerInUse() && this.parent.matgridRowClick)
      this.parent.onMatGridRowClick(event);
    else
      this.matgridRowClick.emit(event);
  }

  onMatGridColClick(event) {
    if (this.isContainerInUse() && this.parent.matgridColClick)
      this.parent.onMatGridColClick(event);
    else
      this.matgridColClick.emit(event);
  }

  onMatGridRowActionClick(event) {
    if (this.isContainerInUse() && this.parent.matgridRowActionClick)
      this.parent.onMatGridRowActionClick(event);
    else
      this.matgridRowActionClick.emit(event);
  }

  onMatGridAddRowButtonClick(event) {
    if (this.isContainerInUse() && this.parent.matgridAddRowButtonClick)
      this.parent.onMatGridAddRowButtonClick(event);
    else
      this.matgridAddRowButtonClick.emit(event);
  }

  onTableLazyLoad(event) {
    if (this.isContainerInUse() && this.parent.tableLazyLoad)
      this.parent.onTableLazyLoad(event);
    else
      this.tableLazyLoad.emit(event);
  }

  onButtonClick(event) {
    if (this.isContainerInUse() && this.parent.buttonClick)
      this.parent.onButtonClick(event);
    else
      this.buttonClick.emit(event);
  }

  onControlClick(event) {
    if (this.isContainerInUse() && this.parent.controlClick) 
      this.parent.onControlClick(event);
    else
      this.controlClick.emit(event);
  }

  onControlChange(event) {
    if (this.isContainerInUse() && this.parent.controlChange)
      this.parent.onControlChange(event);
    else
      this.controlChange.emit(event);
  }

  onKeyUp(event) {
    if (this.isContainerInUse() && this.parent.keyUp)
      this.parent.onKeyUp(event);
    else
      this.keyUp.emit(event);
  }

  onKeydownEnter(event) {
    if (this.isContainerInUse() && this.parent.keydownEnter)
      this.parent.onKeydownEnter(event);
    else
      this.keydownEnter.emit(event);
  }

  onKeydownTab(event) {
    if (this.isContainerInUse() && this.parent.keydownTab)
      this.parent.onKeydownTab(event);
    else
      this.keydownTab.emit(event);
  }

  onFileUploadSuccessClick(event) {
    if (this.isContainerInUse() && this.parent.fileUploadSuccessClick)
      this.parent.onFileUploadSuccessClick(event);
    else
      this.fileUploadSuccessClick.emit(event);
  }

  onFileRemoveClick(event) {
    if (this.isContainerInUse() && this.parent.fileRemoveClick)
      this.parent.onFileRemoveClick(event);
    else
      this.fileRemoveClick.emit(event);
  }

  getFormGroup(): FormGroup {
    if (this.panelSettings == null)
      return null;

    let formGrp: FormGroup = this.panelSettings.getForm();
    try {
      if (formGrp == null) // to prevent from getting form error we create an empty group place
        formGrp = new FormGroup({});
    }
    catch (e) {
      console.log(e);
    }

    return formGrp;
  }

  getActionClass(ctrl: IcecontrolSettings): string {
    return ctrl.properties && ctrl.properties.class ? ctrl.properties.class : '';    
  }

  isLoading(): boolean {
    return this.panelSettings.isLoading();
  }

  ///////////////////////
  // private functions //
  ///////////////////////

  private setPermission(): void {
    // pass on section permission down to controls, groups and tables
    let panelPermission = this.permission;
    if (this.headerControls && this.headerControls.length > 0) {
      this.headerControls.forEach(function (control: IcecontrolSettings) {
        control.permissions = control.permissions ? control.permissions : panelPermission;
      });
    }

    if (this.sections && this.sections.length > 0) {
      this.sections.forEach(function (section: IcepanelsectionSettings) {
        section.permissions = section.permissions ? section.permissions : panelPermission;
      });
    }
  }
}
